
import { Component, Vue, Prop } from 'vue-property-decorator';
import ColorModuleHeader from '@/components/global/color-module/ColorModuleHeader.vue';
import ColorModuleTable from '@/components/global/color-module/ColorModuleTable.vue';
import ColorModuleRequest from '@/components/global/color-module/ColorModuleRequest.vue';
import ColorModuleResult from '@/components/global/color-module/ColorModuleResult.vue';
import { EventBus } from '@/helpers/EventBusHelper';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import Color from '@/models/Color';
import LatestColor from '@/models/LatestColor';
import { ColorData } from '@/interfaces/components/color/ColorData';
import {
    formatCustomColor,
    formatColorSearchQuery,
    FiltersConfiguration,
    MappedColorCollections,
} from '@/helpers/ColorModule/ColorModuleHelper';
import { Color as ColorInfo } from '@/interfaces/components/color/Color';
import { ColorRequestConfig } from '@/interfaces/components/color/ColorRequestConfig';
import { ColorFilters } from '@/interfaces/components/color/ColorFilters';
import { LoadingOverlayHelper } from '@/helpers/LoadingOverlayHelper';
import { formatSelectedColorForCustomRequest } from '@/helpers/ColorModule/ColorModuleHelper';
import ColorModuleTitle from '@/components/global/color-module/ColorModuleTitle.vue';
import ColorModuleWelcomeModal from '@/components/global/popup/ColorModuleWelcomeModal.vue';
import { ColorPowder } from '@/interfaces/components/color/ColorPowder';
import { validChecklistFieldsForColorModule as fields } from '@/regex/validChecklistFieldsForColorModule';

@Component({
    name: 'ColorModule',
    components: {
        ColorModuleHeader,
        ColorModuleTable,
        ColorModuleRequest,
        ColorModuleResult,
        ColorModuleTitle,
        ColorModuleWelcomeModal,
    },
})
export default class ColorModule extends Vue {
    private visible: boolean = false;
    private infoModalVisible: boolean = false;
    private pagination = {
        total: 10,
        current: 1,
        pageSizeOptions: ['10', '20', '50', '100'],
        showSizeChanger: true,
        pageSize: 10,
    };
    private colorSystemPID: string = '';
    private manualInputFieldValue: string = 'ROČNI VPIS';
    private dropdownValueWhereCMWasOpened: string = '';
    private activeProductFormId: number = 0;
    private activeTab: string = 'color-search';
    private loadingOverlay = new LoadingOverlayHelper(this, {});
    private filters: ColorFilters = {
        colorGloss: { id: '', value: '' },
        colorSurface: { id: '', value: '' },
        colorQuality: { id: '', value: '' },
        colorEffect: { id: '', value: '' },
        colorSystem: { id: '', value: '' },
        colorCollection: { id: '', value: '' },
    };
    private requestForm: ColorRequestConfig = {
        colorName: '',
        colorProducer: '',
        powderID: '',
    };
    private validationErrors: { [key: string]: string } = {
        colorName: '',
        colorProducer: '',
        powderID: '',
    };
    private searchQuery: string = '';
    private filtersActive: boolean = false;
    private requestFiltersActive: boolean = false;
    private showLatestColors: boolean = false;
    private moduleLoaded = false;
    private productName: string = '';
    private directory: string = '';

    private get paginationData() {
        if (!this.moduleLoaded) {
            return false;
        }

        return this.pagination;
    }

    private get checklistPIDs() {
        switch (this.colorSystemPID) {
            case fields.SYSTEM_COLOR:
                return {
                    manualColorSelectionPID: 'p74022',
                    standardColorsDropdownPID: 'p74019',
                    nonStandardColorsDropdownPID: 'p74020',
                };
            case fields.COLOR_OF_THE_FINISHING_LAMELLA:
                return {
                    manualColorSelectionPID: 'p1035',
                    standardColorsDropdownPID: 'p1032',
                    nonStandardColorsDropdownPID: 'p1033',
                };
            case fields.COLOR_OF_THE_INSECT_SCREEN:
                return {
                    manualColorSelectionPID: 'p10067',
                    standardColorsDropdownPID: 'p10064',
                    nonStandardColorsDropdownPID: 'p10065',
                };
            case fields.OMARICA_ROLLER_SHUTTER_BOX_COLOR:
                return {
                    manualColorSelectionPID: 'p40182',
                    standardColorsDropdownPID: 'p40179',
                    nonStandardColorsDropdownPID: 'p40180',
                };
            case fields.OMARICA_BACK_COLOR:
                return {
                    manualColorSelectionPID: 'p10383',
                    standardColorsDropdownPID: 'p10380',
                    nonStandardColorsDropdownPID: 'p10381',
                };
            case fields.COLOR_OF_THE_FLUSHMOUNTED_F_CORNER:
                return {
                    manualColorSelectionPID: 'p9094',
                    standardColorsDropdownPID: 'p9091',
                    nonStandardColorsDropdownPID: 'p9092',
                };
            case fields.OMARICA_SHEET_METAL_COLOR:
                return {
                    manualColorSelectionPID: 'p110213',
                    standardColorsDropdownPID: 'p110210',
                    nonStandardColorsDropdownPID: 'p110211',
                };
            case fields.COLOR_OF_THE_FLUSHMOUNTED_CORNER:
                return {
                    manualColorSelectionPID: 'p110220',
                    standardColorsDropdownPID: 'p110217',
                    nonStandardColorsDropdownPID: 'p110218',
                };
            case fields.VODILA_GUIDE_COLOR:
                return {
                    manualColorSelectionPID: 'p40123',
                    standardColorsDropdownPID: 'p40120',
                    nonStandardColorsDropdownPID: 'p40121',
                };
            case fields.COLOR_OF_THE_MOSQUITOS_NET_GUIDES:
                return {
                    manualColorSelectionPID: 'p82706',
                    standardColorsDropdownPID: 'p82703',
                    nonStandardColorsDropdownPID: 'p82704',
                };
            case fields.OMARICA_SERVICE_COVER_COLOR:
                return {
                    manualColorSelectionPID: 'p10389',
                    standardColorsDropdownPID: 'p10386',
                    nonStandardColorsDropdownPID: 'p10388',
                };
            case fields.OMARICA_COVER_COLOR:
                return {
                    manualColorSelectionPID: 'p10370',
                    standardColorsDropdownPID: 'p10367',
                    nonStandardColorsDropdownPID: 'p10368',
                };
            case fields.MASKA:
                return {
                    manualColorSelectionPID: 'p9088',
                    standardColorsDropdownPID: 'p9085',
                    nonStandardColorsDropdownPID: 'p9084',
                };
            case fields.BLIND_COLOR:
                return {
                    manualColorSelectionPID: 'p740581',
                    standardColorsDropdownPID: 'p740578',
                    nonStandardColorsDropdownPID: 'p740579',
                };
            default:
                return {
                    manualColorSelectionPID: 'p74022',
                    standardColorsDropdownPID: 'p74019',
                    nonStandardColorsDropdownPID: 'p74020',
                };
        }
    }

    // $t('Color representations are based on RGB values and may differ from the original')
    private get requestColor() {
        return formatCustomColor(this.requestForm, this.filters);
    }

    private get currentCollection() {
        return this.filters.colorCollection.value;
    }

    private async openPopup(payload?: {
        colorSystemPID: string;
        fieldValue: string;
        activeProductFormId: number;
        productName?: string;
        productCategory?: string;
    }) {
        if (payload) {
            this.colorSystemPID = payload.colorSystemPID;
            this.dropdownValueWhereCMWasOpened = payload.fieldValue;
            this.activeProductFormId = payload.activeProductFormId;
            this.productName = payload.productName || '';
            this.directory = payload.productCategory || '';
        }

        this.visible = true;

        if (!this.moduleLoaded) {
            this.loadModule();
        }
    }

    private async loadModule() {
        try {
            this.loadingOverlay.start();
            this.moduleLoaded = true;
            this.registerInfoModal();

            await this.fetchAndRegisterColorsData();
            this.setDefaultValues();
            await this.fetchColors();
        } catch {
            return Promise.reject();
        } finally {
            this.loadingOverlay.stop();
        }
    }

    private registerInfoModal() {
        const infoModalShown = localStorage.getItem('colorModuleInfoModalShown');

        if (!infoModalShown) {
            this.infoModalVisible = true;
            localStorage.setItem('colorModuleInfoModalShown', 'true');
        }
    }

    private closePopup() {
        this.visible = false;
        // this.moduleLoaded = false;
        // reset forms
        // this.requestForm = {
        //     colorName: '',
        //     colorProducer: '',
        //     powderID: '',
        // };
        // this.filters = {
        //     colorGloss: { id: '', value: '' },
        //     colorSurface: { id: '', value: '' },
        //     colorQuality: { id: '', value: '' },
        //     colorEffect: { id: '', value: '' },
        //     colorSystem: { id: '', value: '' },
        //     colorCollection: { id: '', value: '' },
        // };
    }

    private onTableConfigChange(pagination: any, filters: any, sorter: any) {
        this.pagination.current = pagination.current as number;
        this.pagination.pageSize = pagination.pageSize as number;
    }

    private setPaginationData(totalItems: number) {
        this.pagination.total = totalItems;
    }

    private changeActiveTab(tab: string) {
        this.activeTab = tab;
    }

    private setFiltersActiveState(active: boolean) {
        if (this.activeTab === 'color-request') {
            this.requestFiltersActive = active;
            return;
        }

        this.filtersActive = active;
    }

    private clearFilters() {
        // check if filters are already cleared
        if (!Object.values(this.filters).some((filter) => filter.id || filter.value) && this.searchQuery === '') {
            return;
        }

        this.filters = {
            colorGloss: { id: '', value: '' },
            colorSurface: { id: '', value: '' },
            colorQuality: { id: '', value: '' },
            colorEffect: { id: '', value: '' },
            colorSystem: { id: '', value: '' },
            colorCollection: { id: '', value: '' },
        };

        this.searchQuery = '';
        this.fetchColors();
    }

    private selectColor(colorData: {
        color: ColorInfo;
        selectedPowder: ColorPowder;
        isRoltekColor: boolean;
        groupName: keyof typeof MappedColorCollections;
    }) {
        LatestColor.createNew({
            colorId: colorData?.color?.id || null,
            powderId: colorData?.selectedPowder?.id || null,
        });

        if (this.colorSystemPID === 'material') {
            EventBus.$emit(EventBusEvents.selectMaterialColor, colorData);
            this.closePopup();
            return;
        }

        if (!colorData?.isRoltekColor) {
            const formattedColor = formatSelectedColorForCustomRequest(colorData.color, colorData.selectedPowder);
            this.sendCustomColorRequest(formattedColor, true);

            return;
        }

        const fieldGroup = MappedColorCollections[colorData.groupName];

        const colorDropdownPID =
            colorData.groupName === 'STANDARD'
                ? this.checklistPIDs.standardColorsDropdownPID
                : this.checklistPIDs.nonStandardColorsDropdownPID;

        const payload = {
            activeProductFormId: this.activeProductFormId,
            colorSystemPID: this.colorSystemPID,
            colorDropdownPID: colorDropdownPID,
            colorCollection: fieldGroup,
            selectedColorValue: colorData.color.ingeniousColor,
        };

        this.emitColorChange(payload);
        this.closePopup();
    }

    private sendCustomColorRequest(selectedColor: string = '', shouldFreezeManualInput: boolean = false) {
        const validateFields = this.validateRequestForm();

        if (!validateFields && this.activeTab === 'color-request') {
            this.$notification.error({
                message: 'Validation Error',
                description: Object.values(this.validationErrors)
                    .filter((val) => !!val)
                    .join('\n'),
            });
            return;
        }

        const payload = {
            activeProductFormId: this.activeProductFormId,
            colorSystemPID: this.colorSystemPID,
            colorDropdownPID: this.checklistPIDs.manualColorSelectionPID,
            colorCollection: this.manualInputFieldValue,
            selectedColorValue: selectedColor || this.requestColor,
            shouldFreezeManualInput: shouldFreezeManualInput,
        };

        this.emitColorChange(payload);
        this.closePopup();
    }

    private emitColorChange(payload: any) {
        if (this.activeProductFormId === null) {
            EventBus.$emit(EventBusEvents.updateMultipositionHeaderSystemColorValue, payload);
        } else {
            EventBus.$emit(EventBusEvents.updateSystemColorValue, payload);
        }
    }

    private validateRequestForm() {
        if (!this.requestForm.colorName) {
            this.validationErrors.colorName = this.$t('Color name is required!') as string;
        } else {
            this.validationErrors.colorName = '';
        }

        if (!this.requestForm.colorProducer) {
            this.validationErrors.colorProducer = (this.$t('Color producer is required!') +
                ' ' +
                this.$t('or')) as string;
        } else {
            this.validationErrors.colorProducer = '';
        }

        if (!this.requestForm.powderID) {
            this.validationErrors.powderID = this.$t("Manufacturer's powder ID is required!") as string;
        } else {
            this.validationErrors.powderID = '';
        }

        return !Object.values(this.validationErrors).some((error) => error);
    }

    private onFilterChange({ value, type, id }: { value: string; type: keyof ColorFilters; id: string }) {
        this.filters[type] = { id, value };

        if (this.activeTab === 'color-search') {
            this.fetchColors();
        }
    }

    private searchColors({ query }: { query: string }) {
        this.searchQuery = query;
    }

    private async toggleLatestColors(val: boolean) {
        this.showLatestColors = val;

        if (val === true) {
            const colors = await LatestColor.getAll();
            const latestColors = await this.$store.dispatch('colorData/setLatestColors', { latestColors: colors });

            // Set pagination total to the number of latest colors
            this.setPaginationData(latestColors);
        }
    }

    private async fetchColors() {
        // Since powders are the most important part of the color module, we will fetch them instead of colors and include colors in the response
        try {
            this.loadingOverlay.start();

            const query = formatColorSearchQuery(this.filters, '');
            const [latestColors, colors] = await Promise.all([LatestColor.getAll(), Color.getPowders(query)]);

            this.$store.dispatch('colorData/setColorsData', { colors });
            this.$store.dispatch('colorData/setLatestColors', { latestColors });

            this.setPaginationData(colors.length);
        } catch (error) {
            console.error(error);
        } finally {
            this.loadingOverlay.stop();
        }
    }

    private async fetchAndRegisterColorsData() {
        const requests: Promise<ColorData>[] = [
            // Color.getAll(),
            // Color.getPowders(), we don't need to fetch all powders here, we will fetch them separetely in fetchColors
            Color.getPowderPrices(),
            Color.getSurfaces(),
            Color.getQualities(),
            Color.getEffects(),
            Color.getColorCharts(),
            Color.getColorCollections(),
        ];

        try {
            const results = await Promise.allSettled(requests);
            const [prices, surfaces, qualities, effects, charts, collections] = results.map((result) =>
                result.status === 'fulfilled' ? result.value : [],
            );

            this.$store.dispatch('colorData/setColorsData', {
                prices,
                surfaces,
                qualities,
                effects,
                charts,
                collections,
            });
        } catch (error) {
            console.error(error);
        }
    }

    private setDefaultValues() {
        FiltersConfiguration.forEach((filter) => {
            if (filter?.defaultValue) {
                const selectedOption = filter.options().find((option: any) => option.id === filter.defaultValue);
                const value = selectedOption?.value || selectedOption?.groupName || '';
                this.filters[filter.type as keyof ColorFilters] = { id: selectedOption.id, value };
            }
        });
    }

    private async mounted() {
        EventBus.$on(EventBusEvents.showColorModule, this.openPopup);
    }

    private beforeDestroy() {
        EventBus.$off(EventBusEvents.showColorModule, this.openPopup);
    }
}
